import React, { useCallback, useEffect, useState } from 'react';
import '../client-create-panel/client-create-panel.css';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import './client-bulk-edit-panel.css';

const PRIVILEGE_UNCHANGED = '__unchanged__';
const PRIVILEGE_AUTO = '__auto__';

const PRIVILEGE_OPTIONS = [
  { value: PRIVILEGE_UNCHANGED, label: 'Не изменять' },
  { value: PRIVILEGE_AUTO, label: 'Авто (по тратам и правилам ЛК)' },
  { value: 'bronze', label: 'Бронза' },
  { value: 'silver', label: 'Серебро' },
  { value: 'gold', label: 'Золото' },
];

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const normalizePrivilegeOverride = (raw) => {
  if (raw == null || String(raw).trim() === '') {
    return PRIVILEGE_AUTO;
  }
  const low = String(raw).trim().toLowerCase();
  if (low === 'bronze' || low === 'silver' || low === 'gold') {
    return low;
  }
  return PRIVILEGE_AUTO;
};

const normalizeDiscount = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(n * 100) / 100));
};

const getCommonValue = (items, getter) => {
  if (!items.length) {
    return { mixed: false, value: undefined };
  }
  const first = getter(items[0]);
  const mixed = items.some((item) => getter(item) !== first);
  return { mixed, value: first };
};

const ToggleRow = ({ label, checked, onChange, indeterminate }) => {
  const handleToggleClick = () => {
    if (indeterminate) {
      onChange(false);
      return;
    }
    onChange(!checked);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleClick();
    }
  };

  const ariaLabel = indeterminate
    ? `${label}. Смешанно: у клиентов разные значения. Выключить для всех выбранных`
    : label;

  return (
    <div className="cbep__toggle-row">
      <span className="cbep__toggle-label">{label}</span>
      <button
        type="button"
        className={`cbep__toggle${checked ? ' cbep__toggle--on' : ''}${indeterminate ? ' cbep__toggle--indeterminate' : ''}`}
        onClick={handleToggleClick}
        onKeyDown={handleKeyDown}
        role="switch"
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-label={ariaLabel}
        tabIndex={0}
      >
        <span className="cbep__toggle-dot" />
      </button>
    </div>
  );
};

const ClientBulkEditPanel = ({
  visible = false,
  selectedCount = 0,
  clients = [],
  submitting = false,
  onClose = null,
  onSubmit = null,
}) => {
  const [skipForbidden, setSkipForbidden] = useState(false);
  const [skipForbiddenMixed, setSkipForbiddenMixed] = useState(false);
  const [skipForbiddenDirty, setSkipForbiddenDirty] = useState(false);
  const [privilegeTier, setPrivilegeTier] = useState(PRIVILEGE_UNCHANGED);
  const [privilegeDirty, setPrivilegeDirty] = useState(false);
  const [discountDraft, setDiscountDraft] = useState('');
  const [discountDirty, setDiscountDirty] = useState(false);
  const [error, setError] = useState('');

  const handleClose = useCallback(() => {
    if (!submitting && onClose) {
      onClose();
    }
  }, [submitting, onClose]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    const handleDocumentKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [visible, handleClose]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const skipCommon = getCommonValue(clients, (c) => Boolean(c.skip_forbidden_resources));
    setSkipForbidden(Boolean(skipCommon.value));
    setSkipForbiddenMixed(skipCommon.mixed);
    setSkipForbiddenDirty(false);

    const privilegeCommon = getCommonValue(clients, (c) => normalizePrivilegeOverride(c.privilege_tier_override));
    setPrivilegeTier(privilegeCommon.mixed || privilegeCommon.value == null ? PRIVILEGE_UNCHANGED : privilegeCommon.value);
    setPrivilegeDirty(false);

    const discountCommon = getCommonValue(clients, (c) => normalizeDiscount(c.personal_discount_percent));
    setDiscountDraft(discountCommon.mixed || discountCommon.value == null ? '' : String(discountCommon.value));
    setDiscountDirty(false);
    setError('');
  }, [visible, clients]);

  const handleSkipChange = useCallback((next) => {
    setSkipForbidden(Boolean(next));
    setSkipForbiddenMixed(false);
    setSkipForbiddenDirty(true);
  }, []);

  const handlePrivilegeChange = useCallback((e) => {
    setPrivilegeTier(e.target.value);
    setPrivilegeDirty(true);
    if (error) {
      setError('');
    }
  }, [error]);

  const handleDiscountChange = useCallback((e) => {
    setDiscountDraft(e.target.value);
    setDiscountDirty(true);
    if (error) {
      setError('');
    }
  }, [error]);

  const handleSubmit = useCallback(() => {
    if (!onSubmit) {
      return;
    }

    const payload = {};

    if (skipForbiddenDirty) {
      payload.skip_forbidden_resources = Boolean(skipForbidden);
    }

    if (privilegeDirty && privilegeTier !== PRIVILEGE_UNCHANGED) {
      payload.privilege_tier_override = privilegeTier === PRIVILEGE_AUTO ? '' : privilegeTier;
    }

    if (discountDirty) {
      const discountNormalized = String(discountDraft).replace(',', '.').trim();
      if (discountNormalized !== '') {
        const discountValue = Number(discountNormalized);
        if (Number.isNaN(discountValue)) {
          setError('Введите корректную персональную скидку');
          return;
        }
        if (discountValue < 0 || discountValue > 100) {
          setError('Персональная скидка должна быть в диапазоне 0..100');
          return;
        }
        payload.personal_discount_percent = Math.round(discountValue * 100) / 100;
      }
    }

    if (!Object.keys(payload).length) {
      setError('Измените хотя бы одно поле');
      return;
    }

    setError('');
    onSubmit(payload);
  }, [
    onSubmit,
    skipForbiddenDirty,
    skipForbidden,
    privilegeDirty,
    privilegeTier,
    discountDirty,
    discountDraft,
  ]);

  // Затемнение закрывает форму только настоящим щелчком — нажатие и отпускание на нём (просьба владельца 28.10)
  const overlayBackdrop = useBackdropClose(handleClose);

  const handleOverlayKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="ccp-overlay"
      {...overlayBackdrop}
      onKeyDown={handleOverlayKeyDown}
    >
      <div className="ccp" role="dialog" aria-label="Массовое редактирование клиентов" aria-modal="true">
        <div className="ccp__header">
          <h2 className="ccp__title">массовое редактирование ({selectedCount})</h2>
          <button
            type="button"
            className="ccp__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="ccp__body">
          <div className="ccp__section">
            <ToggleRow
              label="Не использовать запреты"
              checked={skipForbidden}
              indeterminate={skipForbiddenMixed && !skipForbiddenDirty}
              onChange={handleSkipChange}
            />

            <div className="ccp__field">
              <div className="ccp__label">
                <span className="ccp__label-text">Уровень привилегий (ЛК)</span>
              </div>
              <select
                className="ccp__text-input cbep__select"
                value={privilegeTier}
                onChange={handlePrivilegeChange}
                aria-label="Уровень привилегий для выбранных клиентов"
                tabIndex={0}
              >
                {PRIVILEGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="ccp__field">
              <div className="ccp__label">
                <span className="ccp__label-text">Персональная скидка, %</span>
              </div>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className={`ccp__text-input${error && error.includes('скидк') ? ' ccp__text-input--error' : ''}`}
                placeholder="Не изменять"
                value={discountDraft}
                onChange={handleDiscountChange}
                aria-label="Персональная скидка выбранных клиентов, %"
                tabIndex={0}
              />
            </div>

            {error ? <span className="ccp__field-error" role="alert">{error}</span> : null}

            <div className="ccp__info-box">
              <p className="ccp__info-box-text">
                Пустые поля и «Не изменять» не применятся. Смешанный тумблер — кликните, чтобы задать одно значение всем.
              </p>
            </div>
          </div>
        </div>

        <div className="ccp__footer">
          <button
            type="button"
            className="ccp__btn ccp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Отмена"
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="ccp__btn ccp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-label="Сохранить массовое редактирование"
            tabIndex={0}
          >
            {submitting ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientBulkEditPanel;
